import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../../styles/styles.css';
import '../../styles/custom-fixes.css';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { FILE_DOWNLOAD_PREFIX, getBlogDetailUrl } from '../../helpers/url_helper';
import SiteHeader from '../../components/SiteHeader';
import Footer from './Footer';
import useSeoMeta from '../SeoPages/useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import './Blog.css';

const getBlogImageUrl = (path) => {
  if (!path) return null;
  const value = String(path).trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const encoded = value
    .replace(/^\/+/, '')
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${FILE_DOWNLOAD_PREFIX}${encoded}`;
};

const formatBlogDate = (isoDate) => {
  if (!isoDate) return '';
  try {
    return new Date(isoDate).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const truncateText = (text, max = 160) => {
  if (!text) return '';
  const clean = String(text).trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max).trim()}…`;
};

function BlogImage({ src, alt, className }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className={`${className} blog-image-fallback`} aria-hidden="true">
        <span>Complimate</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function Blog() {
  const { mode } = useThemeMode();
  useSeoMeta(getPublicSeoPage('/blog/'));

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  useEffect(() => {
    let cancelled = false;

    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dataAPI.getBlogs({ showLoading: true, loadingMessage: 'Loading blogs...' });
        if (cancelled) return;

        if (response.status === 'success' && Array.isArray(response.data)) {
          setBlogs(response.data);
        } else {
          setError('Failed to load blog posts.');
        }
      } catch (err) {
        console.error('Error fetching blogs:', err);
        if (!cancelled) setError('Failed to load blog posts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBlogs();
    return () => {
      cancelled = true;
    };
  }, []);

  const featured = blogs[0] || null;
  const rest = blogs.slice(1);

  return (
    <div
      style={{
        backgroundColor: mode === 'dark' ? '#111' : '#fff',
        minHeight: '100vh',
      }}
    >
      <SiteHeader activeNav="blog" />

      <section className={`blog-section ${mode === 'dark' ? 'blog-section-dark' : ''}`}>
        <div className="blog-hero">
          <Container>
            <p className="blog-eyebrow">Insights &amp; updates</p>
            <h1 className="blog-title">Complimate Blog</h1>
            <p className="blog-subtitle">
              Practical WHS guidance, product updates, and compliance insights for Australian teams.
            </p>
          </Container>
        </div>

        <Container className="blog-content pb-5">
          {loading && (
            <div className="blog-status-wrap" aria-busy="true">
              <div className="blog-skeleton blog-skeleton-featured" />
              <Row className="g-4 mt-1">
                <Col md={6}><div className="blog-skeleton blog-skeleton-card" /></Col>
                <Col md={6}><div className="blog-skeleton blog-skeleton-card" /></Col>
              </Row>
            </div>
          )}

          {error && !loading && (
            <p className="blog-status blog-status-error text-center">{error}</p>
          )}

          {!loading && !error && blogs.length === 0 && (
            <p className="blog-status text-center">No blog posts available yet.</p>
          )}

          {!loading && !error && featured && (
            <>
              <article className="blog-featured">
                <Link
                  to={getBlogDetailUrl(featured.slug)}
                  className="blog-featured-media-link"
                  aria-label={featured.title}
                >
                  <div className="blog-featured-media">
                    <BlogImage
                      src={getBlogImageUrl(featured.article_image || featured.article_thumbnail)}
                      alt={featured.title}
                      className="blog-featured-img"
                    />
                  </div>
                </Link>
                <div className="blog-featured-body">
                  <div className="blog-meta">
                    {featured.category?.name && (
                      <span className="blog-category">{featured.category.name}</span>
                    )}
                    <time dateTime={featured.created_at}>{formatBlogDate(featured.created_at)}</time>
                    {featured.created_by && (
                      <span className="blog-author">by {featured.created_by}</span>
                    )}
                  </div>
                  <h2 className="blog-featured-title">
                    <Link to={getBlogDetailUrl(featured.slug)}>{featured.title}</Link>
                  </h2>
                  <p className="blog-featured-desc">{truncateText(featured.description, 220)}</p>
                  <Link
                    to={getBlogDetailUrl(featured.slug)}
                    className="blog-read-link"
                  >
                    Read article
                    <span aria-hidden="true"> →</span>
                  </Link>
                </div>
              </article>

              {rest.length > 0 && (
                <Row className="g-4 blog-grid">
                  {rest.map((post) => (
                    <Col key={post.id} xs={12} md={6} lg={4}>
                      <article className="blog-card h-100">
                        <Link
                          to={getBlogDetailUrl(post.slug)}
                          className="blog-card-media-link"
                          aria-label={post.title}
                        >
                          <BlogImage
                            src={getBlogImageUrl(post.article_thumbnail || post.article_image)}
                            alt=""
                            className="blog-card-img"
                          />
                        </Link>
                        <div className="blog-card-body">
                          <div className="blog-meta">
                            {post.category?.name && (
                              <span className="blog-category">{post.category.name}</span>
                            )}
                            <time dateTime={post.created_at}>{formatBlogDate(post.created_at)}</time>
                          </div>
                          <h3 className="blog-card-title">
                            <Link to={getBlogDetailUrl(post.slug)}>{post.title}</Link>
                          </h3>
                          <p className="blog-card-desc">{truncateText(post.description, 140)}</p>
                          <Link
                            to={getBlogDetailUrl(post.slug)}
                            className="blog-read-link blog-read-link-sm"
                          >
                            Read article
                            <span aria-hidden="true"> →</span>
                          </Link>
                        </div>
                      </article>
                    </Col>
                  ))}
                </Row>
              )}
            </>
          )}
        </Container>
      </section>

      <Footer />
    </div>
  );
}

export default Blog;
